import {
  Modal,
  ModalProps,
  Form,
  Select,
  Row,
  Col,
  Spin,
  message,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import SurveysService from '../SurveysService';
import { FieldData } from 'rc-field-form/es/interface';
import ApiError from '../../core/ApiError';
import { MailOutlined, QrcodeOutlined } from '@ant-design/icons';
import styles from './qradminmodal.module.css';
import { isInRole } from '../../auth/components/RenderForRole';

const { Paragraph } = Typography;

const getFullUrl = (pathName: string) => {
  const path = pathName.endsWith('?')
    ? pathName.substring(0, pathName.length - 1)
    : pathName;
  const url = new URL(path, window.location.href);
  return url.toString();
};

type QRAdminModalProps = ModalProps & {
  client: string;
  survey?: any;
  filters?: any;
};

const QRAdminModal = ({
  survey,
  client,
  filters,
  ...modalProps
}: QRAdminModalProps) => {
  const [loading, setLoading] = useState(false);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [query, setQuery] = useState<string>('');
  const [configs, setConfigs] = useState<any[]>([]);
  const [selected, setSelected] = useState<number>(0);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [urlQuery, setUrlQuery] = useState<string>('');

  const onFiltersChange = (changed: FieldData[], all: FieldData[]) => {
    const q = all
      .filter((a) => a.value)
      .map(
        (a) =>
          `${encodeURIComponent(a.name.toString())}=${encodeURIComponent(
            a.value
          )}`
      )
      .join('&');
    setQuery(q);
  };

  const onQueryChange = (changed: FieldData[], all: FieldData[]) => {
    const q = all
      .filter((a) => a.value)
      .map(
        (a) =>
          `${encodeURIComponent(a.name.toString())}=${encodeURIComponent(
            a.value
          )}`
      )
      .join('&');
    setUrlQuery(q);
    console.log(q);
  };

  useEffect(() => {
    const func = async () => {
      try {
        setLoading(true);
        const tags = await SurveysService.getSurveyTags(client, survey.id);
        console.log('these are the tags', tags);
        setTags(tags);
      } catch (err) {
        console.error(err);
        message.error((err as Error).message);
      }
      setLoading(false);
    };
    if (survey && survey.id) func();
  }, [survey, client]);

  useEffect(() => {
    if (!query) {
      setQuery(
        `${Object.keys(filters || {})
          .map(
            (k) => `${encodeURIComponent(k)}=${encodeURIComponent(filters[k])}`
          )
          .join('&')}`
      );
    }
  }, [query, filters, survey, client]);

  useEffect(() => {
    const func = async () => {
      try {
        setConfigsLoading(true);
        const data = await SurveysService.getConfigs(
          client,
          survey.uuid,
          1,
          10000,
          query
        );
        setConfigs(data.results);
      } catch (err) {
        console.error(err);
        message.error((err as Error).message);
      }
      setConfigsLoading(false);
    };
    func();
  }, [query, survey, client]);

  const openModal = (item: any, idx: number) => {
    setSelected(idx);
    setModalVisible(true);
  };

  const downloadQRs = async () => {
    try {
      setFileLoading(true);
      let anchor = document.createElement('a');
      document.body.appendChild(anchor);

      const blob = await SurveysService.downloadQRs(survey.uuid, query);
      let objectUrl = window.URL.createObjectURL(blob);

      anchor.href = objectUrl;
      anchor.download = `QRs - ${survey.title}.${
        blob.type === 'application/zip'
          ? 'zip'
          : blob.type === 'image/jpeg'
          ? 'jpg'
          : 'png'
      }`;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data.statusCode === 404) {
          message.error('No se encontraron QRs con los filtros seleccionados');
        } else {
          message.error(err.message);
        }
      } else message.error('Ocurrió un error al descargar el reporte');
      console.error(err);
    }

    setFileLoading(false);
  };

  return (
    <Modal
      width='80vw'
      confirmLoading={fileLoading}
      destroyOnClose
      title='Administración de códigos'
      okButtonProps={{ hidden: true }}
      cancelText='Cerrar'
      {...modalProps}
      onOk={downloadQRs}
    >
      <strong>{survey.title}</strong>
      <p>Selecciona los filtros de los QRs que quieres buscar</p>
      <Row gutter={32}>
        <Col span={8}>
          <Form
            id='frmRptFlt'
            layout='vertical'
            onFieldsChange={onFiltersChange}
          >
            <Row gutter={8}>
              {loading && (
                <Col span={24}>
                  <Spin spinning>Cargando...</Spin>
                </Col>
              )}
              {!loading &&
                tags
                  .filter((t) => t.type === 'tag')
                  .map((tag) => (
                    <Col key={tag.id} span={24}>
                      <Form.Item
                        name={tag.name}
                        label={tag.name}
                        initialValue={
                          filters?.hasOwnProperty(tag.name)
                            ? filters[tag.name] || undefined
                            : undefined
                        }
                      >
                        <Select mode='multiple' placeholder='Todos'>
                          {tag.values.map((value: any) => (
                            <Select.Option key={value} value={value}>
                              {value}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  ))}
            </Row>
          </Form>
        </Col>
        <Col span={16}>
          <Spin spinning={configsLoading}>
            <div className={styles.totals}>
              {configs?.length ?? 0} QRs (
              {(configs ?? []).filter((c: any) => !c.enabled).length}{' '}
              deshabilitados)
            </div>
            <div className={styles.cont}>
              {(configs || []).map((item: any, idx: number) => (
                <div
                  key={item.id}
                  className={`${styles.item} ${
                    !item.enabled ? styles.itemDisabled : ''
                  }`}
                  onClick={() => openModal(item, idx)}
                >
                  {item.type === 'qr' ? <QrcodeOutlined /> : <MailOutlined />}{' '}
                  {item.title}{' '}
                  {!item.enabled && <Tag color='warning'>Deshabilitado</Tag>}
                </div>
              ))}
            </div>
          </Spin>
        </Col>
      </Row>
      {configs.length > 0 ? (
        <Modal
          title={configs[selected].title}
          visible={modalVisible}
          wrapClassName={styles.modal}
          onCancel={() => setModalVisible(false)}
        >
          <div style={{ width: 200, margin: 'auto' }}>
            <Form id='frmRptQry' onFieldsChange={onQueryChange}>
              {!loading &&
                tags
                  .filter((t) => t.type === 'query')
                  .map((tag) => (
                    <Form.Item name={tag.id} label={tag.name}>
                      <Select placeholder={`${tag.name}: No especificado`}>
                        <Select.Option value=''>No especificado</Select.Option>
                        {tag.values.map((value: any) => (
                          <Select.Option
                            key={value}
                            value={value.split(';')[1]}
                          >
                            {value.split(';')[0]}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ))}
            </Form>
          </div>

          {(configs[selected].type === 'qr' || isInRole(['superadmin'])) && (
            <>
              <img
                src={`/api/surveys/qr/${configs[selected].uuid}?${urlQuery}`}
                alt='Código QR de la configuración'
              />
              <a
                className='ant-btn ant-btn-primary'
                href={`/api/surveys/qr/${configs[selected].uuid}?${urlQuery}`}
                download={`${configs[selected].title}.svg`}
              >
                Descargar QR
              </a>
            </>
          )}

          {(configs[selected].type !== 'qr' || isInRole(['superadmin'])) && (
            <div className={styles.urlSection}>
              <h2>URL</h2>
              <Paragraph copyable>
                {getFullUrl(`/surveys/${configs[selected].uuid}?${urlQuery}`)}
              </Paragraph>
            </div>
          )}

          {isInRole(['superadmin']) && (
            <div className={styles.urlSection}>
              <h2>URL Pruebas</h2>
              <Paragraph copyable>
                {getFullUrl(
                  `/surveys/test/${configs[selected].uuid}?${urlQuery}`
                )}
              </Paragraph>
            </div>
          )}
        </Modal>
      ) : null}
    </Modal>
  );
};

export default QRAdminModal;
